import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, of } from "rxjs";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { IUserData } from "../../interfaces/administration/userData";
import { CalAngularService } from "@cvx/cal-angular";
import { RoleNames } from "../../utils/role-names";
import { EntraPrefix } from "../../utils/entra-prefixes";
import { UserService } from "../administration/user.service";
import { FacilityService } from "../administration/facilities/facilities.service";
import { Router } from "@angular/router";

@Injectable({
  providedIn: "root",
})
export class LoggedUserService {
  private loggedUsername: string = "";
  private loggedUserDataSubject = new BehaviorSubject<ILoggedUserData | null>(
    null
  );
  public loggedUserData$: Observable<ILoggedUserData | null> =
    this.loggedUserDataSubject.asObservable();

  private loggedUserRoleSubject = new BehaviorSubject<string | null>(null);
  public loggedUserRole$: Observable<string | null> =
    this.loggedUserRoleSubject.asObservable();

  constructor(
    private userService: UserService,
    private authService: CalAngularService,
    private facilityService: FacilityService,
    private router: Router
  ) {}

  setLoggedUserData(data: ILoggedUserData): void {
    this.loggedUserDataSubject.next(data);
  }

  setLoggedUserRoleData(data: string): void {
    this.loggedUserRoleSubject.next(data);
  }

  getLoggedUserRoleFromToken(): Observable<string> {
    const currentValue = this.loggedUserRoleSubject.getValue();

    if (currentValue !== null) {
      return this.loggedUserRole$;
    } else {
      const loggedUserRole = this.getLoggedUserRole();

      this.setLoggedUserRoleData(loggedUserRole);
      return this.loggedUserRole$;
    }
  }

  getLoggedUserData(): Observable<ILoggedUserData> {
    const currentValue = this.loggedUserDataSubject.getValue();

    if (currentValue !== null) {
      return this.loggedUserData$;
    } else {
      let accountInfo = this.authService.getAccount();
      this.loggedUsername = encodeURIComponent(accountInfo.username);
      return this.userService.getUserByEmail(this.loggedUsername).pipe(
        map((data) => {
          // Check if user data exists
          if (!data) {
            console.error("User not found in the system");
            this.router.navigate(["/no-access"]);
            throw new Error("User not found");
          }

          const userData: IUserData = data;
          const loggedUserData: ILoggedUserData = {
            id: userData.id,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            facilityId: userData.facilityId,
            facilityName: userData.facilityName,
            role: this.loggedUserRoleSubject?.value ?? RoleNames.ERT_USER,
          };
          this.setLoggedUserData(loggedUserData);
          return loggedUserData;
        }),
        catchError((error) => {
          console.error("Error fetching user data:", error);
          this.router.navigate(["/no-access"]);
          return of(null as any); // Return observable that completes
        })
      );
    }
  }

  getLoggedUserDataFromApi(): Observable<ILoggedUserData> {
    let accountInfo = this.authService.getAccount();
    this.loggedUsername = accountInfo.username;
    return this.userService.getUserByEmail(this.loggedUsername).pipe(
      map((data) => {
        const userData: IUserData = data;
        const loggedUserData: ILoggedUserData = {
          id: userData.id,
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          facilityId: userData.facilityId,
          facilityName: userData.facilityName,
          role: this.loggedUserRoleSubject?.value ?? RoleNames.ERT_USER,
        };

        this.setLoggedUserData(loggedUserData);
        return loggedUserData;
      })
    );
  }

  getLoggedUserRole(): string {
    const roles = this.getClaimsFromToken();
    if (roles.find((role) => role == RoleNames.ERT_ADMINISTRATOR)) {
      return RoleNames.ERT_ADMINISTRATOR;
    } else {
      return RoleNames.ERT_USER;
    }
  }

  getLoggedUserFacilityEntraNameFromToken(): string {
    const facilityName = this.getClaimsFromToken().find((claim) =>
      claim.includes(EntraPrefix.FACILITY)
    );
    return facilityName ? facilityName.split("_")[2] : "";
  }

  getLoggedUserFacilityName(): Observable<string> {
    let entraName = this.getLoggedUserFacilityEntraNameFromToken();
    if (!entraName) {
      console.error("No facility name found in user claims");
      this.router.navigate(["/no-access"]);
      return of("");
    }

    return this.facilityService.getFacilityByEntraName(entraName).pipe(
      map((facility) => {
        if (!facility) {
          console.error("Facility not found for entraName:", entraName);
          this.router.navigate(["/no-access"]);
          return "";
        }
        return facility.name;
      }),
      catchError((error) => {
        console.error("Error fetching facility name: ", error);
        this.router.navigate(["/no-access"]);
        return of("");
      })
    );
  }
  private getClaimsFromToken(): string[] {
    const idTokenClaims = this.authService.getIdTokenClaims();
    const claims =
      typeof idTokenClaims === "string"
        ? JSON.parse(idTokenClaims)
        : idTokenClaims;
    return claims.roles || [];
  }
}
